import { useEffect, useState } from "react";
import { Copy, TrendingUp, DollarSign, MousePointer, ShoppingBag, Check, Gift, Trophy, Banknote, Share2, Instagram, Video, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const FREE_YEAR_GOAL = 15;

const STATUS_PILL = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  reversed: "bg-rose-50 text-rose-800 border-rose-200",
};

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card-flat p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--ff-text-muted)]">
        <Icon size={14} />
        <span className="text-xs uppercase tracking-[0.18em]">{label}</span>
      </div>
      <p className="mt-3 font-display text-4xl text-[var(--ff-primary)]">{value}</p>
      {sub && <p className="mt-1 text-xs text-[var(--ff-text-muted)]">{sub}</p>}
    </div>
  );
}

export default function AmbassadorDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedCap, setCopiedCap] = useState(null);
  const [wStatus, setWStatus] = useState("idle"); // idle | sending | done | error
  const [wMsg, setWMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/ambassador/me");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load your ambassador dashboard.");
    }
  };

  useEffect(() => { load(); }, []);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Seo title="Ambassador Dashboard · The Farm To You" path="/ambassador/dashboard" />
        <p className="text-[var(--ff-text-secondary)]" data-testid="ambassador-dash-err">{err}</p>
        <a href="/ambassadors" className="mt-6 inline-flex btn-secondary">Apply to become an Ambassador</a>
      </div>
    );
  }
  if (!data) {
    return <div className="p-16 text-center text-[var(--ff-text-muted)]">Loading your dashboard…</div>;
  }

  const { profile, counts, earnings, recent_commissions: recent = [] } = data;
  const refUrl = profile?.referral_code ? `${window.location.origin}/ref/${profile.referral_code}` : "";
  const signups = counts.signups || 0;
  const toFreeYear = Math.max(0, FREE_YEAR_GOAL - signups);
  const pct = Math.min(100, Math.round((signups / FREE_YEAR_GOAL) * 100));

  const copy = async () => {
    if (!refUrl) return;
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const copyCaption = async (i, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCap(i);
      setTimeout(() => setCopiedCap(null), 1800);
    } catch { /* clipboard blocked */ }
  };

  const captions = [
    { platform: "Instagram", icon: <Instagram size={13} />, text: `Real beef & chicken from American family ranches — not mystery grocery-store meat. 🇺🇸\n\nI teamed up with The Farm To You so you get $10 off your membership with my link 👇\n${refUrl}\n\n#thefarmtoyou #grassfed #realfood #americanraised` },
    { platform: "TikTok", icon: <Video size={13} />, text: `POV: you stop buying mystery meat 🥩 The Farm To You ships real American-raised beef & chicken straight to your door. Use my link for $10 off 👉 ${refUrl} #fyp #beef #realfood #thefarmtoyou` },
    { platform: "Story / short", icon: <Share2 size={13} />, text: `Know exactly where your meat comes from. 🐄 $10 off your Farm To You membership with my link: ${refUrl}` },
  ];

  const withdraw = async () => {
    setWStatus("sending"); setWMsg("");
    try {
      const { data: res } = await api.post("/ambassador/withdraw", {});
      setWStatus("done");
      setWMsg(`Withdrawal of ${fmtUsd(res.withdrawal?.amount_usd)} requested — we'll process it to your account.`);
      load();
    } catch (e) {
      setWStatus("error");
      setWMsg(e.response?.data?.detail || "Could not request a withdrawal right now.");
    }
  };

  return (
    <div className="bg-[var(--ff-bg)] min-h-screen">
      <Seo title="Ambassador Dashboard · The Farm To You" path="/ambassador/dashboard" />
      <div className="max-w-5xl mx-auto px-6 py-16">
        <p className="overline text-[var(--ff-secondary)]">Ambassador console</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[var(--ff-text)]">Welcome, {(profile.name || "Ambassador").split(" ")[0]}.</h1>
        <p className="mt-3 text-[var(--ff-text-secondary)] max-w-2xl">
          Share your link — your friends get <strong>$10 off</strong> their membership, and you earn <strong>10% of every membership</strong> they buy. Paid out via {profile.payout_method || "your chosen method"}.
        </p>

        {/* Black Friday social kit CTA */}
        <Link
          to="/social-kit"
          className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-[var(--ff-primary)]/25 bg-[var(--ff-accent-kraft)]/20 px-6 py-5 hover:border-[var(--ff-primary)] transition-colors group"
          data-testid="ambassador-social-kit-link"
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--ff-primary)] text-white flex-shrink-0">
              <Sparkles size={18} />
            </span>
            <div>
              <p className="font-display text-xl text-[var(--ff-primary)]">Black Friday social kit</p>
              <p className="text-sm text-[var(--ff-text-secondary)]">Ready-made launch graphics &amp; captions with your link baked in.</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-[var(--ff-primary)] group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </Link>

        {/* This month momentum */}        <section className="mt-8 rounded-2xl bg-[var(--ff-primary)] text-white px-6 py-7 flex flex-wrap items-center justify-between gap-6" data-testid="ambassador-month-card">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--ff-citrus)]">Earned this month</p>
            <p className="mt-2 font-display text-5xl" data-testid="ambassador-month-earned">{fmtUsd(earnings.this_month_usd)}</p>
            <p className="mt-1 text-sm text-white/75">{fmtUsd(earnings.pending_usd)} pending · {fmtUsd(earnings.lifetime_usd)} lifetime</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-white/70">Payout threshold</p>
            <p className="font-display text-2xl mt-1">{fmtUsd(earnings.payout_threshold_usd || 25)}</p>
            <p className="mt-1 text-xs text-white/75" data-testid="ambassador-payout-progress">
              {(earnings.pending_usd || 0) >= (earnings.payout_threshold_usd || 25)
                ? "Ready to withdraw 🎉"
                : `${fmtUsd((earnings.payout_threshold_usd || 25) - (earnings.pending_usd || 0))} to go`}
            </p>
          </div>
        </section>

        {/* Reward explainer */}
        <section className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="ambassador-rewards">
          <div className="card-flat p-5">
            <Gift size={18} className="text-[var(--ff-primary)]" />
            <p className="mt-3 font-display text-lg text-[var(--ff-text)]">$10 off for friends</p>
            <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Everyone who joins with your code saves $10 on their membership.</p>
          </div>
          <div className="card-flat p-5">
            <DollarSign size={18} className="text-[var(--ff-primary)]" />
            <p className="mt-3 font-display text-lg text-[var(--ff-text)]">10% to you</p>
            <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Earn 10% of the membership value on every qualifying signup.</p>
          </div>
          <div className="card-flat p-5">
            <Trophy size={18} className="text-[var(--ff-primary)]" />
            <p className="mt-3 font-display text-lg text-[var(--ff-text)]">15 = a free year</p>
            <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Refer 15 members and your next year of membership is on us. Otherwise, enjoy $10 off your renewal.</p>
          </div>
        </section>

        {/* Free-year progress */}
        <section className="mt-6 card-flat p-6" data-testid="ambassador-freeyear-progress">
          <div className="flex items-end justify-between">
            <p className="overline text-[var(--ff-secondary)]">Progress to a free year</p>
            <p className="text-sm text-[var(--ff-text-secondary)]">{signups}/{FREE_YEAR_GOAL} signups</p>
          </div>
          <div className="mt-3 h-3 w-full rounded-full bg-[var(--ff-muted)] overflow-hidden">
            <div className="h-full rounded-full bg-[var(--ff-primary)] transition-all" style={{ width: `${pct}%` }} data-testid="freeyear-bar" />
          </div>
          <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">
            {toFreeYear === 0
              ? "🎉 You've unlocked a free yearly membership! We'll apply it to your next renewal."
              : `${toFreeYear} more signup${toFreeYear === 1 ? "" : "s"} to earn a free yearly membership.`}
          </p>
        </section>

        {/* Referral link card */}
        <section className="mt-6 card-flat p-6 sm:p-8" data-testid="ambassador-ref-card">
          <p className="overline text-[var(--ff-secondary)]">Your personal referral link</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
            <code className="flex-1 truncate rounded-lg bg-[var(--ff-muted)] px-4 py-3 font-mono text-sm border border-[var(--ff-border)]" data-testid="ambassador-ref-url">
              {refUrl || "Pending — promo code mint in progress"}
            </code>
            <button onClick={copy} disabled={!refUrl} className="btn-primary inline-flex items-center gap-2" data-testid="ambassador-ref-copy">
              {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy link</>}
            </button>
          </div>
          <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
            Short code: <strong className="font-mono">{profile.referral_code || "—"}</strong>
            {profile.stripe_promo_minted
              ? " · $10 off applied automatically at checkout."
              : " · $10 promo code will be activated within 1 hour of approval."}
          </p>
        </section>

        {/* Share kit */}
        <section className="mt-6 card-flat p-6 sm:p-8" data-testid="ambassador-share-kit">
          <div className="flex items-center gap-2 text-[var(--ff-primary)]"><Share2 size={18} /><p className="font-display text-xl">Share kit</p></div>
          <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">Ready-to-post captions with your link baked in. Tap to copy, then paste into Instagram or TikTok.</p>
          <div className="mt-5 space-y-3">
            {captions.map((cap, i) => (
              <div key={i} className="rounded-xl border border-[var(--ff-border)] bg-[var(--ff-muted)]/40 p-4" data-testid={`share-caption-${i}`}>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--ff-text-muted)]">{cap.icon}{cap.platform}</div>
                <p className="mt-2 text-sm text-[var(--ff-text)] whitespace-pre-line leading-relaxed">{cap.text}</p>
                <button
                  onClick={() => copyCaption(i, cap.text)}
                  disabled={!refUrl}
                  className="btn-secondary mt-3 inline-flex items-center gap-2 text-sm"
                  data-testid={`share-caption-copy-${i}`}
                >
                  {copiedCap === i ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy caption</>}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ambassador-stats">
          <Stat icon={MousePointer} label="Reach (clicks)" value={counts.clicks} testid="stat-clicks" />
          <Stat icon={TrendingUp} label="Signups" value={counts.signups} testid="stat-signups" />
          <Stat icon={ShoppingBag} label="Qualifying memberships" value={counts.orders} testid="stat-orders" />
          <Stat icon={DollarSign} label="Lifetime earnings" value={fmtUsd(earnings.lifetime_usd)} sub={`${fmtUsd(earnings.pending_usd)} pending · ${fmtUsd(earnings.paid_usd)} paid`} testid="stat-earnings" />
        </section>

        {/* Withdrawal */}
        <section className="mt-6 card-flat p-6" data-testid="ambassador-withdraw">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[var(--ff-primary)]"><Banknote size={18} /><p className="font-display text-xl">Bank withdrawal</p></div>
              <p className="mt-1 text-sm text-[var(--ff-text-secondary)]">
                You have <strong>{fmtUsd(earnings.pending_usd)}</strong> available to withdraw.
              </p>
            </div>
            <button
              onClick={withdraw}
              disabled={wStatus === "sending" || (earnings.pending_usd || 0) <= 0}
              className="btn-primary disabled:opacity-50"
              data-testid="ambassador-withdraw-btn"
            >
              {wStatus === "sending" ? "Requesting…" : "Request withdrawal"}
            </button>
          </div>
          {wMsg && (
            <p className={`mt-3 text-sm ${wStatus === "error" ? "text-rose-700" : "text-emerald-700"}`} data-testid="ambassador-withdraw-msg">{wMsg}</p>
          )}
        </section>

        {/* Recent commissions */}
        <section className="mt-12">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl text-[var(--ff-text)]">Recent earnings</h2>
            <p className="text-xs uppercase tracking-[0.18em] text-[var(--ff-text-muted)]">Last 25</p>
          </div>
          {recent.length === 0 ? (
            <div className="mt-6 card-flat p-10 text-center text-[var(--ff-text-secondary)]" data-testid="ambassador-empty">
              <p>No earnings yet. Share your link to start earning.</p>
            </div>
          ) : (
            <div className="mt-6 card-flat overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="ambassador-commissions-table">
                <thead className="bg-[var(--ff-muted)] text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Date</th>
                    <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Type</th>
                    <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Membership value</th>
                    <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Your cut (10%)</th>
                    <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--ff-border)]">
                  {recent.map((c) => (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                      <td className="px-4 py-3 text-[var(--ff-text)]">{fmtDate(c.created_at)}</td>
                      <td className="px-4 py-3 text-[var(--ff-text-secondary)] capitalize">{c.kind}</td>
                      <td className="px-4 py-3 text-right text-[var(--ff-text-secondary)]">{fmtUsd(c.gross_revenue)}</td>
                      <td className="px-4 py-3 text-right font-medium text-[var(--ff-text)]">{fmtUsd(c.commission_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs ${STATUS_PILL[c.status] || ""}`}>
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="mt-12 text-xs text-[var(--ff-text-muted)]">
          You earn 10% of the membership value on every qualifying referral. Referred friends get $10 off their membership automatically at checkout. Refer 15 members in a year to earn a free yearly membership; otherwise enjoy $10 off your own renewal.
        </p>
      </div>
    </div>
  );
}
